import commaNumber from 'comma-number';
import { Calendar, Info } from 'lucide-react';
import { useMemo } from 'react';

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/shadcn/ui/popover';
import {
  convertStructuredFilterOperatorToFilterOperator,
  STRUCTURED_FILTER_OPERATOR,
  structuredFilterOperatorDic,
} from '@/entities/(filter)/common';
import { Badge } from '@/shared/ui';
import zIndex from '@/styles/zIndex';
import { getFilterOperatorText } from '@/types/dataTableConst';
import {
  FEEDBACK_COLUMNS,
  FeedbackColumnNameMap,
} from '@/types/filterKeyConst';
import { DATE_FORMAT, getTimezoneDayjs } from '@/utils/time';

const getFilterValueLabel = (filterValue: FilterKeyValue) => {
  return filterValue.filterName.toString() || filterValue.filterKey.toString();
};

const getFilterOperatorLabel = (filter: StructuredFilterQueryFormatT) => {
  if (filter.operator === STRUCTURED_FILTER_OPERATOR.NOR) {
    return structuredFilterOperatorDic[filter.operator];
  }

  return getFilterOperatorText({
    operator: convertStructuredFilterOperatorToFilterOperator(filter.operator),
    isMultiValueField: true,
    multiFilter: filter.filterValues.length > 1,
  });
};

const isStructuredFilterQueryFormatArray = (
  value: unknown,
): value is StructuredFilterQueryFormatT[] => {
  return Array.isArray(value);
};

const getColumnName = (columnKey: string) => {
  return (
    FeedbackColumnNameMap.toArray().find((entry) => entry.value === columnKey)
      ?.label || null
  );
};

const StaticFilterSummary = (props: {
  formats: StructuredFilterQueryFormatT[];
}) => {
  const { dateRange, filterFormats } = useMemo(() => {
    const startDateFilter = props.formats.find(
      (filter) => filter.columnKey === FEEDBACK_COLUMNS.DATE_START,
    );
    const endDateFilter = props.formats.find(
      (filter) => filter.columnKey === FEEDBACK_COLUMNS.DATE_END,
    );

    return {
      dateRange:
        startDateFilter?.filterValues[0] && endDateFilter?.filterValues[0]
          ? {
              startDate: getFilterValueLabel(startDateFilter.filterValues[0]),
              endDate: getFilterValueLabel(endDateFilter.filterValues[0]),
            }
          : null,
      filterFormats: props.formats.filter(
        (filter) =>
          filter.columnKey !== FEEDBACK_COLUMNS.DATE_START &&
          filter.columnKey !== FEEDBACK_COLUMNS.DATE_END,
      ),
    };
  }, [props.formats]);

  if (props.formats.length === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-2">
      {dateRange && (
        <Badge
          className="items-center gap-2 px-3 py-1 text-xs font-medium"
          variant="outline"
        >
          <Calendar className="size-3.5" />
          <span>
            {getTimezoneDayjs(dateRange.startDate).format(DATE_FORMAT.readable)}{' '}
            - {getTimezoneDayjs(dateRange.endDate).format(DATE_FORMAT.readable)}
          </span>
        </Badge>
      )}
      {filterFormats.map((filter, index) => {
        const columnName = getColumnName(filter.columnKey);

        if (!columnName) return null;

        return (
          <Badge
            key={`${filter.columnKey}-${filter.operator}-${index}`}
            className="items-center gap-2 px-3 py-1 text-xs font-medium"
            variant="outline"
          >
            <span>{columnName}</span>
            <span className="px-1 text-muted-foreground">
              {getFilterOperatorLabel(filter)}
            </span>
            <span className="max-w-40 truncate">
              {filter.filterValues.map(getFilterValueLabel).join(', ')}
            </span>
          </Badge>
        );
      })}
    </div>
  );
};

export const AnalyzedFeedbackCountDisplay = (props: {
  feedbackCount: number;
  filter_string: string;
  isItem: boolean;
}) => {
  const { feedbackCount } = props;

  const filterQueryFormats = useMemo(() => {
    if (!props.filter_string) return [];

    try {
      const parsed = JSON.parse(props.filter_string);
      return isStructuredFilterQueryFormatArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }, [props.filter_string]);

  return (
    <Popover>
      <PopoverTrigger>
        <div className="flex items-center gap-1 p-2 px-2 font-medium text-gray-500 cursor-pointer">
          {props.isItem
            ? 'Created from chat'
            : `Analyzed ${commaNumber(feedbackCount)} feedback items`}

          <Info className="text-gray-500 size-4.5 ml-0.5" />
        </div>
      </PopoverTrigger>
      <PopoverContent
        className="flex flex-col gap-5 w-100"
        style={{ zIndex: zIndex.tooltip }}
      >
        <StaticFilterSummary formats={filterQueryFormats} />
        <span className="text-xs text-muted-foreground">
          This answer is generated by finding information related to your
          question within the filtered feedback. By adjusting the analysis scope
          through filters, you can focus on the most relevant data and obtain
          more accurate and desired answers.
        </span>
      </PopoverContent>
    </Popover>
  );
};
